import { useEffect, useRef } from 'react'

import type { GradeKind } from '@/lib/api/tea-grade-api'
import { getAnalyticsActor, posthog } from '@/lib/posthog'

/** Kết quả người dùng thấy sau một từ khoá đã gõ xong. */
export type TeaGradeSearchResult = 'has_match' | 'empty'

/**
 * Chỉ gửi enum + actor: KHÔNG gửi từ khoá người dùng gõ (có thể là tên riêng
 * của hộ trồng), không id/tên phân loại. Contract:
 * `docs/analytics/tea-grade-stock.md`. Lỗi analytics không được chặn việc tìm.
 */
function capture(result: TeaGradeSearchResult, catalog: GradeKind) {
    const identity = getAnalyticsActor()
    if (!identity) return
    try {
        posthog.capture('tea_grade_list_searched', {
            // v2 (M-39): thêm `catalog` — mỗi kho có danh mục riêng.
            schema_version: 2,
            catalog,
            stage: 'searched',
            result,
            ...identity,
        })
    } catch {
        // Analytics failure must not block searching.
    }
}

interface SearchState {
    /** Từ khoá đã debounce (giá trị đang nằm trên URL), không phải từng phím gõ. */
    query: string
    /** Số dòng còn lại sau khi lọc. */
    matchCount: number
    /** Danh mục chưa tải xong — chưa biết kết quả nên chưa được kết luận. */
    isLoading: boolean
    /** Danh mục của kho nào (M-39). Mặc định kho phân loại. */
    catalog?: GradeKind
}

/**
 * Đo ô tìm của màn Loại phân loại: người dùng có tìm không, và tìm xong có ra
 * gì không. `empty` nhiều bất thường nghĩa là danh mục thiếu loại mà người dùng
 * đang cần — chính là ca "A160/A150 không có trên hệ thống".
 *
 * Một từ khoá phát đúng một lần: gõ tiếp thành từ khoá khác mới tính lượt mới.
 */
export function useTeaGradeSearchTracking({
    query,
    matchCount,
    isLoading,
    catalog = 'sorting',
}: SearchState) {
    // Từ khoá đã báo. Xoá trắng ô tìm thì quên đi, để người dùng gõ lại đúng
    // từ khoá cũ vẫn được tính là một lượt tìm mới.
    const reportedFor = useRef<string | null>(null)

    useEffect(() => {
        const q = query.trim()
        if (!q) {
            reportedFor.current = null
            return
        }
        // Đang tải mà báo `empty` là báo sai: chưa có dữ liệu để lọc, không
        // phải danh mục không có loại đó.
        if (isLoading) return
        if (reportedFor.current === q) return
        reportedFor.current = q
        capture(matchCount > 0 ? 'has_match' : 'empty', catalog)
    }, [query, matchCount, isLoading, catalog])
}
